import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import LanguageMultiSelect from '../../components/common/LanguageMultiSelect';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { localInputToUtcIso } from '../../utils/dateTime';
import { normalizeApiError } from '../../utils/errorMessages';
import { MEETING_LANGUAGE_OPTIONS, parseLanguageCsv } from '../../utils/languageOptions';
import { 
  Calendar, 
  Users, 
  FileText, 
  Image as ImageIcon,
  User,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Upload,
  X,
  Search,
  AlertCircle
} from 'lucide-react';

const CreateMeeting = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [speakers, setSpeakers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    language: [],
    description: '',
    youtubeStreamUrl: '',
    startTime: '',
    endTime: '',
    bannerUrl: '',
    speakerIds: [],
  });
  const [bannerPreview, setBannerPreview] = useState('');
  const [speakerSearch, setSpeakerSearch] = useState('');
  const [speakerLanguageFilter, setSpeakerLanguageFilter] = useState('all');
  const [speakerSpecializationFilter, setSpeakerSpecializationFilter] = useState('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingSpeakers, setFetchingSpeakers] = useState(true);

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const fetchSpeakers = async () => {
    try {
      setFetchingSpeakers(true);
      const response = await api.get('/admin/speakers');
      setSpeakers(response.data.filter(s => s.isActive));
    } catch (error) {
      console.error('Failed to fetch speakers:', error);
    } finally {
      setFetchingSpeakers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSpeakerToggle = (speakerId) => {
    setFormData(prev => ({
      ...prev,
      speakerIds: prev.speakerIds.includes(speakerId)
        ? prev.speakerIds.filter(id => id !== speakerId)
        : [...prev.speakerIds, speakerId]
    }));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveBanner = () => {
    setBannerPreview('');
    setFormData({ ...formData, bannerUrl: '' });
  };

  const filteredSpeakers = speakers.filter((speaker) => {
    const matchesSearch =
      speaker.name.toLowerCase().includes(speakerSearch.toLowerCase()) ||
      speaker.email.toLowerCase().includes(speakerSearch.toLowerCase());
    const matchesLanguage =
      speakerLanguageFilter === 'all' ||
      parseLanguageCsv(speaker.language || '').map((item) => item.toLowerCase()).includes(speakerLanguageFilter.toLowerCase());
    const matchesSpecialization =
      speakerSpecializationFilter === 'all' ||
      (speaker.specialization || '').toLowerCase() === speakerSpecializationFilter.toLowerCase();

    return matchesSearch && matchesLanguage && matchesSpecialization;
  });

  const availableSpeakerLanguages = Array.from(
    new Set(speakers.flatMap((speaker) => parseLanguageCsv(speaker.language || '')))
  ).sort((a, b) => a.localeCompare(b));

  const availableSpecializations = Array.from(
    new Set(speakers.map((speaker) => (speaker.specialization || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        startTime: localInputToUtcIso(formData.startTime),
        endTime: formData.endTime ? localInputToUtcIso(formData.endTime) : null,
        bannerUrl: bannerPreview || formData.bannerUrl,
      };
      
      await api.post('/meetings', submitData);
      navigate('/admin/meetings');
    } catch (err) {
      const errorMessage = normalizeApiError(err, 'Failed to create meeting');
      setError(errorMessage);
      // Scroll to top to show error banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '32px',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: theme.colors.textSecondary,
      fontSize: '14px',
      marginBottom: '16px',
      cursor: 'pointer',
      transition: 'color 0.2s',
      background: 'none',
      border: 'none',
      padding: '0',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
    },
    errorBanner: {
      position: 'sticky',
      top: '0',
      zIndex: 50,
      padding: '16px 32px',
      backgroundColor: theme.colors.error + '15',
      color: theme.colors.error,
      border: `1px solid ${theme.colors.error}30`,
      fontSize: '15px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: theme.shadows.sm,
    },
    formContainer: {
      padding: '32px',
    },
    section: {
      marginBottom: '28px',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
    },
    fullWidth: {
      gridColumn: '1 / -1',
    },
    speakerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      maxHeight: '400px',
      overflowY: 'auto',
      padding: '4px',
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '8px',
      backgroundColor: theme.colors.backgroundSecondary,
    },
    speakerCard: (selected) => ({
      padding: '10px 12px',
      borderRadius: '6px',
      border: `1.5px solid ${selected ? theme.colors.primary : theme.colors.border}`,
      backgroundColor: selected ? theme.colors.primary + '08' : theme.colors.cardBg,
      cursor: 'pointer',
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }),
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      objectFit: 'cover',
      flexShrink: 0,
    },
    avatarPlaceholder: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: theme.colors.primary + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    speakerInfo: {
      flex: 1,
      minWidth: 0,
    },
    speakerName: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '2px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    speakerEmail: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    checkIcon: {
      flexShrink: 0,
    },
    emptyState: {
      padding: '32px',
      textAlign: 'center',
      color: theme.colors.textSecondary,
      fontSize: '14px',
      backgroundColor: theme.colors.backgroundSecondary,
      borderRadius: '8px',
    },
    actions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '32px',
      paddingTop: '24px',
      borderTop: `1px solid ${theme.colors.border}`,
    },
    bannerUpload: {
      border: `2px dashed ${theme.colors.border}`,
      borderRadius: '8px',
      padding: '24px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: theme.colors.backgroundSecondary,
    },
    bannerPreview: {
      position: 'relative',
      marginTop: '12px',
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${theme.colors.border}`,
    },
    bannerImage: {
      width: '100%',
      maxHeight: '200px',
      objectFit: 'cover',
      display: 'block',
    },
    removeButton: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: theme.colors.error,
      color: '#ffffff',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s',
    },
    searchBox: {
      marginBottom: '12px',
    },
    dateTimeInput: {
      position: 'relative',
    },
    speakerCount: {
      fontSize: '13px',
      color: theme.colors.textSecondary,
      marginTop: '8px',
    },
    helperText: {
      marginTop: '6px',
      fontSize: '12px',
      color: theme.colors.textSecondary,
    },
  };

  if (fetchingSpeakers) {
    return <LoadingSpinner fullScreen text="Loading speakers..." />;
  }

  return (
    <div style={styles.container}>
      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={18} style={{ flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}
      <div style={styles.formContainer}>
        <div style={styles.header}>
          <button
            style={styles.backButton}
            onClick={() => navigate('/admin/meetings')}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primary}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            <ArrowLeft size={18} />
            Back to Meetings
          </button>
        </div>

        <Card>
          <form onSubmit={handleSubmit}>

          {/* Basic Information */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <FileText size={20} />
              Basic Information
            </div>
            
            <div style={styles.fullWidth}>
              <Input
                type="text"
                name="title"
                label="Meeting Title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Introduction to React Development"
                required
              />
            </div>

            <div style={styles.formGrid}>
              <Input
                type="text"
                name="topic"
                label="Topic"
                value={formData.topic}
                onChange={handleChange}
                placeholder="e.g., Cardiology"
              />

              <LanguageMultiSelect
                label="Languages"
                options={MEETING_LANGUAGE_OPTIONS}
                value={formData.language}
                onChange={(nextValue) => setFormData((prev) => ({ ...prev, language: nextValue }))}
                helperText="Select one or more languages for this meeting."
              />
            </div>

            <div style={styles.fullWidth}>
              <Input
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Provide a detailed description of the meeting..."
                textarea
                rows={4}
              />
            </div>

            <div style={styles.fullWidth}>
              <Input
                type="url"
                name="youtubeStreamUrl"
                label="YouTube Stream URL (for attendees)"
                value={formData.youtubeStreamUrl}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
                helperText="Optional. Attendees (users) will watch this stream after verification, while admins/speakers join the live meeting room."
              />
            </div>
          </div>

          {/* Schedule */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <Calendar size={20} />
              Schedule
            </div>
            
            <div style={styles.formGrid}>
              <Input
                type="datetime-local"
                name="startTime"
                label="Start Date & Time"
                value={formData.startTime}
                onChange={handleChange}
                icon={Calendar}
                placeholder="Select start date and time"
                required
              />

              <Input
                type="datetime-local"
                name="endTime"
                label="End Date & Time"
                value={formData.endTime}
                onChange={handleChange}
                icon={Calendar}
                placeholder="Select end date and time"
                helperText="Optional - Leave empty if duration is unknown"
              />
            </div>
          </div>

          {/* Banner Image */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <ImageIcon size={20} />
              Banner Image
            </div>
            
            {!bannerPreview ? (
              <div
                style={styles.bannerUpload}
                onClick={() => document.getElementById('bannerInput').click()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                  e.currentTarget.style.backgroundColor = theme.colors.primary + '05';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
                }}
              >
                <Upload size={32} color={theme.colors.textSecondary} style={{ margin: '0 auto 12px' }} />
                <p style={{ margin: '0 0 8px 0', fontWeight: '500', color: theme.colors.text }}>
                  Click to upload banner image
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: theme.colors.textSecondary }}>
                  Recommended 1600x900 (16:9), minimum 1280x720. PNG/JPG up to 5MB.
                </p>
                <input
                  id="bannerInput"
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              <div style={styles.bannerPreview}>
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  style={styles.bannerImage}
                />
                <button
                  type="button"
                  style={styles.removeButton}
                  onClick={handleRemoveBanner}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>

          {/* Assign Speakers */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <Users size={20} />
              Assign Speakers {formData.speakerIds.length > 0 && (
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: theme.colors.primary 
                }}>
                  ({formData.speakerIds.length} selected)
                </span>
              )}
            </div>
            
            {speakers.length === 0 ? (
              <div style={styles.emptyState}>
                <Users size={32} color={theme.colors.textSecondary} style={{ margin: '0 auto 12px' }} />
                <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>
                  No active speakers available
                </p>
                <p style={{ margin: 0, fontSize: '13px' }}>
                  Add speakers from the Speakers management page first.
                </p>
              </div>
            ) : (
              <>
                <div style={styles.searchBox}>
                  <Input
                    type="text"
                    placeholder="Search speakers by name or email..."
                    value={speakerSearch}
                    onChange={(e) => setSpeakerSearch(e.target.value)}
                    icon={Search}
                  />
                </div>

                <div style={{ ...styles.formGrid, marginBottom: '12px' }}>
                  <select
                    value={speakerLanguageFilter}
                    onChange={(e) => setSpeakerLanguageFilter(e.target.value)}
                    style={styles.filterInput}
                  >
                    <option value="all">All Speaker Languages</option>
                    {availableSpeakerLanguages.map((language) => (
                      <option key={language} value={language}>{language}</option>
                    ))}
                  </select>
                  <select
                    value={speakerSpecializationFilter}
                    onChange={(e) => setSpeakerSpecializationFilter(e.target.value)}
                    style={styles.filterInput}
                  >
                    <option value="all">All Specializations</option>
                    {availableSpecializations.map((specialization) => (
                      <option key={specialization} value={specialization}>{specialization}</option>
                    ))}
                  </select>
                </div>
                
                {filteredSpeakers.length === 0 ? (
                  <div style={styles.emptyState}>
                    <Search size={32} color={theme.colors.textSecondary} style={{ margin: '0 auto 12px' }} />
                    <p style={{ margin: 0 }}>No speakers found matching "{speakerSearch}"</p>
                  </div>
                ) : (
                  <>
                    <div style={styles.speakerGrid}>
                      {filteredSpeakers.map(speaker => {
                        const isSelected = formData.speakerIds.includes(speaker.id);
                        return (
                          <div
                            key={speaker.id}
                            style={styles.speakerCard(isSelected)}
                            onClick={() => handleSpeakerToggle(speaker.id)}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = theme.colors.primary + '50';
                                e.currentTarget.style.backgroundColor = theme.colors.primary + '04';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = theme.colors.border;
                                e.currentTarget.style.backgroundColor = theme.colors.cardBg;
                              }
                            }}
                          >
                            {speaker.avatar ? (
                              <img
                                src={speaker.avatar}
                                alt={speaker.name}
                                style={styles.avatar}
                              />
                            ) : (
                              <div style={styles.avatarPlaceholder}>
                                <User size={18} color={theme.colors.primary} />
                              </div>
                            )}
                            <div style={styles.speakerInfo}>
                              <div style={styles.speakerName}>{speaker.name}</div>
                              <div style={styles.speakerEmail}>{speaker.email}</div>
                            </div>
                            <div style={styles.checkIcon}>
                              {isSelected ? (
                                <CheckCircle2 size={20} color={theme.colors.primary} />
                              ) : (
                                <Circle size={20} color={theme.colors.border} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={styles.speakerCount}>
                      Showing {filteredSpeakers.length} of {speakers.length} speakers
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/admin/meetings')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              loading={loading}
            >
              Create Meeting
            </Button>
          </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CreateMeeting;
